import { GeoJsonFeatureFactory, RotationUnit } from '../geojson/GeoJsonFeatureFactory';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import * as olExtent from 'ol/extent';
import LayerGroup from 'ol/layer/Group';
import VectorTile from 'ol/layer/VectorTile';
import { PMTilesVectorSource } from 'ol-pmtiles';
import Static from 'ol/source/ImageStatic';
import { transform as olTransform, Projection } from 'ol/proj';

import { Auth } from '@shared/Auth';
import { ModelService } from '@shared/models/model-service';
import { GeojsonFeatureCollection } from '@shared/geojson/types';
import { IDrawing } from './drawing-types';
import { LoadingManager } from '@shared/models/loading-manager';
import { RateLimiter } from '@shared/utils/rate-limiter';
import XYZ from 'ol/source/XYZ';
import { DrawingLoaders } from './drawing-loaders';
import TileLayer from 'ol/layer/Tile';
import { Geometry } from 'ol/geom';
import ImageLayer from 'ol/layer/Image';
import geojsonvt from 'geojson-vt';
import { GeoJsonUtils } from '../geojson/OlGeojsonUtils';
import { createXYZ, extentFromProjection } from 'ol/tilegrid';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile.js';
import { TreeNode } from '@shared/ui-native/Tree';

const projection = 'EPSG:3857';
const GEOJSON_FORMAT = new GeoJSON({
    featureProjection: projection,
});

const extent = extentFromProjection(projection);
const tileGrid = createXYZ({
    extent: extent,
    maxZoom: 22,
    tileSize: 512,
});

const VECTOR_TILE_FORMAT = new GeoJSON({
    // Data returned from geojson-vt is in tile pixel units
    dataProjection: new Projection({
        code: 'TILE_PIXELS',
        units: 'tile-pixels',
        extent: [0, 0, 4096, 4096],
    }),
});
const BASE_URL: string = ModelService.getVizApiBaseUrl() + '/drawings';
const MAX_RADIAN_VALUE = Math.PI * 2;
const rateLimiter = new RateLimiter(10, 15);

type DrawingConfig = {
    tileMinZoom: number;
    tileMaxZoom: number;
    rasterMinZoom: number;
    rasterMaxZoom: number;
    polygonMinZoom: number;
    polygonMaxZoom: number;
    textMinZoom: number;
    textMaxZoom: number;
};

const MOBILE_CONFIG: DrawingConfig = {
    tileMinZoom: 15,
    tileMaxZoom: 27,
    rasterMinZoom: 1,
    rasterMaxZoom: 20,
    polygonMinZoom: 18,
    polygonMaxZoom: 27,
    textMinZoom: 17,
    textMaxZoom: 27,
};

const DESKTOP_CONFIG: DrawingConfig = {
    tileMinZoom: 16,
    tileMaxZoom: 27,
    rasterMinZoom: 1,
    rasterMaxZoom: 16,
    polygonMinZoom: 17,
    polygonMaxZoom: 27,
    textMinZoom: 17,
    textMaxZoom: 27,
};

export class Drawing implements IDrawing {
    uuid: string;

    wgs84Extent: olExtent.Extent;
    extent: olExtent.Extent;

    pmTilesLayer: VectorTile;
    vectorTileLayer: VectorTileLayer;

    imageLayer: ImageLayer<Static>;
    rasterLayer: TileLayer<XYZ>;
    textLayer: VectorLayer<VectorSource<Geometry>>;
    bigTextLayer: VectorLayer<VectorSource<Geometry>>;
    polygonLayer: VectorLayer<VectorSource<Geometry>>;

    layerGroup: LayerGroup;
    isMobile: boolean;

    node?: TreeNode;

    constructor(uuid: string, node?: TreeNode) {
        this.uuid = uuid;
        this.layerGroup = new LayerGroup({});
        this.node = node;

        const isMobile = window.innerWidth <= 800 || window.location.pathname.includes('mobile');
        console.log('Drawing isMobile:', isMobile);

        this.isMobile = isMobile;
    }

    /*
     ** OVERVIEW:
     ** The load() function implements a multi-tier drawing visualization strategy
     ** that adapts to device capabilities and data availability. It loads drawing
     ** data in layers: raster base layer (either pre-generated tiles or static image),
     ** vector geometry (PMTiles or on-demand tiles), text labels, and polygon fills.
     ** Zoom level ranges are carefully managed to optimize performance on mobile
     ** devices while maintaining visual quality on desktop.
     **
     ** ARCHITECTURE:
     ** The function follows a waterfall loading pattern:
     **   1. Fetch drawing status and determine available data sources
     **   2. Load base raster layer (XYZ tiles or static image)
     **   3. Load vector geometry at appropriate zoom levels
     **   4. Load text and polygon overlays
     **
     ** MOBILE OPTIMIZATION:
     ** On mobile devices, vector tiles are restricted to zoom level 20+ to prevent
     ** browser crashes from excessive geometry. Pre-generated raster tiles provide
     ** visualization at lower zoom levels. This threshold (20) was determined
     ** empirically to balance visual quality against device memory constraints.
     **
     ** FALLBACK STRATEGY:
     ** When pre-generated data is unavailable, the system falls back to:
     **   - Static image instead of XYZ tiles
     **   - Client-side vector tiling (geojson-vt) instead of PMTiles
     ** This ensures drawings remain viewable even when batch processing is incomplete.
     */
    public async load(): Promise<void> {
        const config = this.isMobile ? MOBILE_CONFIG : DESKTOP_CONFIG;

        /* Fetch drawing metadata to determine what data sources are available */
        const status = await ModelService.getDrawingStatus(this.uuid);
        const preGeneratedRasterTilesAvailable: boolean = status.raster_tiles_available;

        /* Establish zoom level boundaries for different layer types */
        const rasterMinZoom = config.rasterMinZoom;
        const tileMinZoom = config.tileMinZoom;
        const rasterMaxZoom = this.isMobile && preGeneratedRasterTilesAvailable ? status.max_raster_zoom : config.rasterMaxZoom;
        const pmTilesUrl: string | null = await ModelService.getPMTilesUrl(this.uuid);

        const vectorTilesAvailable: boolean = status.pmtiles_available && pmTilesUrl !== null;
        const detailedMinZoom = preGeneratedRasterTilesAvailable && this.isMobile ? status.max_raster_zoom : tileMinZoom;

        /* Indicate processing status in the UI tree node if applicable */
        if (this.node && status.is_in_processing) {
            this.node.setSecondaryIcon('iki iki-process');
        }

        /* Initialize debug logging to track layer loading sequence */
        const logLines: string[] = [];
        const name = this.node ? this.node.text : this.uuid;
        logLines.push('##### Loading drawing', name, '#####');

        /* RASTER BASE LAYER:
         ** Attempt to load the foundational raster imagery. This provides
         ** a complete view of the drawing at lower zoom levels before
         ** vector geometry becomes viable.
         */
        let rasterLayerLoaded = false;
        if (preGeneratedRasterTilesAvailable) {
            /* The batch processing has pregenerated a XYZ tile layer for this drawing.
             ** Use it for efficient multi-resolution display. */

            logLines.push(`Loading XYZ tiles as base layer. [${rasterMinZoom} - ${rasterMaxZoom}]`);
            await this.createExtent();
            await this.createRasterTileLayer(status.raster_tile_url, rasterMinZoom, rasterMaxZoom);
            rasterLayerLoaded = true;
        } else {
            /* No pregenerated tiles found. Load one static image instead which
             ** covers the whole drawing. This is less efficient but ensures
             ** the drawing remains visible. */
            logLines.push(`Loading static image as base layer. [${rasterMinZoom} - ${rasterMaxZoom}]`);
            rasterLayerLoaded = await this.createImageLayer(rasterMinZoom, rasterMaxZoom);
        }

        /* VECTOR TILE ZOOM CALCULATION:
         ** Determine the minimum zoom level at which to display vector tiles.
         ** This critical decision balances visual quality against device
         ** performance constraints.
         */
        let vectorTileMinZoomLevel = detailedMinZoom;

        if (!rasterLayerLoaded) {
            /* If no raster base layer could be loaded, show vector tiles from
             ** zoom level 1 to ensure some visualization is available */
            vectorTileMinZoomLevel = 1;
        } else if (this.isMobile && !preGeneratedRasterTilesAvailable) {
            /* On mobile without pre-generated tiles, defer vector tiles to zoom 20
             ** to prevent memory exhaustion from rendering excessive geometry */
            vectorTileMinZoomLevel = 20;
        }

        /* VECTOR GEOMETRY LAYER:
         ** Load the primary geometric data using the most efficient available
         ** source. PMTiles are preferred for their compression and tiled access.
         */
        if (vectorTilesAvailable) {
            /* If we show vector tiles below zoom level 20 on mobile the amount of geometry
             ** loaded could still cause the browser to crash on mobile devices. The threshold 20
             ** isn't based on anything else than trying out different threshold until finding a
             ** good balance. */

            logLines.push(`Loading PMtiles. [${vectorTileMinZoomLevel} - ${27}]`);
            await this.createVectorLayer(pmTilesUrl, vectorTileMinZoomLevel, config.tileMaxZoom);
        } else {
            /* If no PMTiles are available, fall back to on-demand vector tiles. Load the geojson from server and perform
             ** vector tiling in the client using geojson-vt. This is computationally
             ** expensive but provides fallback functionality. */
            logLines.push(`Loading on-demand vector tiles. [${vectorTileMinZoomLevel} - ${27}]`);
            await this.createVectorTileLayer(vectorTileMinZoomLevel, config.tileMaxZoom);
        }

        /* OVERLAY LAYERS:
         ** Load text annotations and polygon fills as separate layers.
         ** These render at high zoom levels for readability.
         */
        logLines.push(`Loading text layers. [${config.textMinZoom} - ${config.textMaxZoom}]`);
        await this.createTextLayer(config.textMinZoom, config.textMaxZoom);
        logLines.push(`Loading polygon layers. [${config.polygonMinZoom} - ${config.polygonMaxZoom}]`);
        await this.createPolygonLayer(config.polygonMinZoom, config.polygonMaxZoom);

        /* Output diagnostic information for debugging layer loading */
        logLines.push('############');
        console.log(logLines.join('\n'));
        console.log(this.extent, this.isExtentEmpty());

        /* ERROR HANDLING:
         ** If the drawing extent is empty (no geometry bounds could be determined),
         ** mark the node as disabled to prevent user interaction with invalid data.
         */
        if (this.isExtentEmpty() && this.node) {
            this.node.setError('Extent is empty');
            this.node.setChecked(0);
            this.node.setEnabled(false);
        }
    }

    private async createVectorTileLayer(minZoom = 16, maxZoom = 24) {
        const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/lines`;

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: { Authorization: 'Bearer ' + token.accessToken },
        });

        if (!resp.ok) {
            return;
        }

        const features: GeojsonFeatureCollection = await resp.json();

        if (this.isExtentEmpty()) {
            // calculate extent from features
            const featureExtents = features.features.map(feature => {
                const olFeature = GEOJSON_FORMAT.readFeature(feature);
                return olFeature.getGeometry().getExtent();
            });

            const drawingExtent = olExtent.createEmpty();
            featureExtents.forEach(extent => {
                olExtent.extend(drawingExtent, extent);
            });

            this.extent = drawingExtent;
        }

        const tileIndex = geojsonvt(features, {
            minZoom: minZoom,
            maxZoom: Math.min(maxZoom, 24),
            tolerance: 5,
            extent: 4096,
        });

        const vectorTileLoader = (tile, src) => {
            const tileCoord = JSON.parse(src);

            const tileData = tileIndex.getTile(tileCoord[0], tileCoord[1], tileCoord[2]);

            const geojson = JSON.stringify(
                {
                    type: 'FeatureCollection',
                    features: tileData ? tileData.features : [],
                },
                GeoJsonUtils.replacer
            );

            const actualFeatures = VECTOR_TILE_FORMAT.readFeatures(geojson, {
                extent: tileGrid.getTileCoordExtent(tileCoord),
                featureProjection: 'EPSG:3857',
            });

            tile.setFeatures(actualFeatures);
        };

        const vectorSource = new VectorTileSource({
            maxZoom: 24,
            tileUrlFunction: tileCoord => JSON.stringify(tileCoord),
            tileLoadFunction: vectorTileLoader,
        });

        this.vectorTileLayer = new VectorTileLayer({
            source: vectorSource,
            maxZoom: 24,
            minZoom: 6,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
        });

        this.layerGroup.getLayers().push(this.vectorTileLayer);
    }

    private async createVectorLayer(pmtilesUrl: string, minZoom: number, maxZoom: number) {
        const source = new PMTilesVectorSource({
            url: pmtilesUrl,
        });

        this.pmTilesLayer = new VectorTile({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            source: source,
            maxZoom: maxZoom,
            minZoom: minZoom,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
            extent: this.extent,
        });

        this.layerGroup.getLayers().push(this.pmTilesLayer);
    }

    private async createRasterTileLayer(url: string, minZoom: number, maxZoom: number) {
        const tileSource = new XYZ({
            url: url,
            tileLoadFunction: async (tile, src) => {
                await DrawingLoaders.imageTileLoader(tile, src);
            },
        });

        this.rasterLayer = new TileLayer({
            source: tileSource,
            minZoom: minZoom,
            maxZoom: maxZoom,
            extent: this.extent,
        });

        this.layerGroup.getLayers().push(this.rasterLayer);
    }

    private async createPolygonLayer(minZoom: number, maxZoom: number) {
        const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/polygons`;

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: { Authorization: 'Bearer ' + token.accessToken },
        });

        if (!resp.ok) {
            return;
        }

        const features: GeojsonFeatureCollection = await resp.json();
        const polygonFeatures = GEOJSON_FORMAT.readFeatures(features, { featureProjection: 'EPSG:3857' });
        const polygonSource = new VectorSource({
            format: GEOJSON_FORMAT,
            features: polygonFeatures,
        });

        this.polygonLayer = new VectorLayer({
            source: polygonSource,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
            minZoom: minZoom,
            maxZoom: maxZoom,
        });

        this.layerGroup.getLayers().push(this.polygonLayer);
    }

    private async createTextLayer(minZoom: number, maxZoom: number) {
        LoadingManager.startLoading(this.uuid + '_text');
        const returnToken: () => void = await rateLimiter.requestToken();
        try {
            const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/text`;

            const token = await Auth.getToken();

            const resp = await fetch(url, {
                headers: { Authorization: 'Bearer ' + token.accessToken },
            });

            if (!resp.ok) {
                return;
            }

            const features: GeojsonFeatureCollection = await resp.json();

            const textFeatures = GEOJSON_FORMAT.readFeatures(features);

            const rotations: number[] = textFeatures.map(feature => feature.get('ROTATION') || 0);

            const isRadians: boolean = rotations.every(rotation => rotation >= 0 && rotation <= MAX_RADIAN_VALUE);

            if (isRadians) {
                textFeatures.forEach(feature => feature.set('ROTATION_UNITS', RotationUnit.RADIANS));
            } else {
                textFeatures.forEach(feature => feature.set('ROTATION_UNITS', RotationUnit.DEGREES));
            }

            const bigTextFeatures = textFeatures.filter(text => text.get('HEIGHT') > 50);

            const vectorSource = new VectorSource({
                features: textFeatures.filter(text => !bigTextFeatures.includes(text)),
            });

            this.textLayer = new VectorLayer({
                source: vectorSource,
                style: GeoJsonFeatureFactory.STYLE.textStyle,
                minZoom: minZoom,
                maxZoom: maxZoom,
            });

            if (bigTextFeatures.length > 0) {
                const bigTextVectorSource = new VectorSource({
                    features: bigTextFeatures,
                });

                this.bigTextLayer = new VectorLayer({
                    source: bigTextVectorSource,
                    style: GeoJsonFeatureFactory.STYLE.textStyle,
                    minZoom: 1,
                    maxZoom: maxZoom,
                });

                this.layerGroup.getLayers().push(this.bigTextLayer);
            }

            this.layerGroup.getLayers().push(this.textLayer);
        } catch (e) {
            console.error(e);
        } finally {
            returnToken();
            LoadingManager.finishLoading(this.uuid + '_text');
        }
    }

    private async createExtent(): Promise<void> {
        const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/img/zoom/16`;

        const token = await Auth.getToken();

        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token.accessToken}`,
            },
        });

        if (!resp.ok) {
            return;
        }

        const data = await resp.json();

        const minCoord = [data.extent[0], data.extent[1]];
        const maxCoord = [data.extent[2], data.extent[3]];
        const min = olTransform(minCoord, 'EPSG:4326', 'EPSG:3857');
        const max = olTransform(maxCoord, 'EPSG:4326', 'EPSG:3857');

        const transformedExtent: olExtent.Extent = olExtent.createEmpty();
        olExtent.extendCoordinates(transformedExtent, [min, max]);

        this.wgs84Extent = [data.extent[0], data.extent[1], data.extent[2], data.extent[3]];
        this.extent = transformedExtent;
    }

    private async createImageLayer(minZoom: number, maxZoom: number): Promise<boolean> {
        const returnToken: () => void = await rateLimiter.requestToken();
        try {
            const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/img/zoom/16`;

            const token = await Auth.getToken();

            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token.accessToken}`,
                },
            });

            const data = await resp.json();

            const minCoord = [data.extent[0], data.extent[1]];
            const maxCoord = [data.extent[2], data.extent[3]];
            const min = olTransform(minCoord, 'EPSG:4326', 'EPSG:3857');
            const max = olTransform(maxCoord, 'EPSG:4326', 'EPSG:3857');

            const transformedExtent: olExtent.Extent = olExtent.createEmpty();
            olExtent.extendCoordinates(transformedExtent, [min, max]);

            this.wgs84Extent = [data.extent[0], data.extent[1], data.extent[2], data.extent[3]];
            this.extent = transformedExtent;
            const source = new Static({
                url: data.url,
                projection: projection,
                imageExtent: transformedExtent,
                crossOrigin: 'anonymous',
            });

            this.imageLayer = new ImageLayer({
                source: source,
                minZoom: minZoom,
                maxZoom: maxZoom,
            });

            this.layerGroup.getLayers().push(this.imageLayer);
            return true;
        } catch (e) {
            console.error(e);
            return false;
        } finally {
            returnToken();
        }
    }

    private isExtentEmpty(): boolean {
        return !this.extent || olExtent.isEmpty(this.extent) || olExtent.getWidth(this.extent) === 0 || olExtent.getHeight(this.extent) === 0;
    }

    public dispose() {
        if (this.pmTilesLayer) {
            this.pmTilesLayer.dispose();
        }

        if (this.vectorTileLayer) {
            this.vectorTileLayer.dispose();
        }

        if (this.rasterLayer) {
            this.rasterLayer.dispose();
        }

        if (this.textLayer) {
            this.textLayer.dispose();
        }

        if (this.bigTextLayer) {
            this.bigTextLayer.dispose();
        }

        if (this.polygonLayer) {
            this.polygonLayer.dispose();
        }

        if (this.imageLayer) {
            this.imageLayer.dispose();
        }

        if (this.node) {
            const layerCounts = this.node.ajaxData.layerCounts;

            if (layerCounts.mapLayers > 0 && layerCounts.features > 0) {
                this.node.setSecondaryIcon('iki iki-map-layers');
            } else {
                this.node.setSecondaryIcon(null);
            }
        }
    }
}
